/*
  Shared WhatsApp helpers — used by the storefront (customer → store)
  and by the admin panel (store → customer).
*/

import { STORE, VENDOR } from './store';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/* Strip formatting and add the India country code when it's a bare
   10-digit number, so wa.me links always resolve. */
export const normalisePhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `91${digits.slice(1)}`;
  return digits;
};

/* Derived from the one place the shop's details live, so the number a
   customer messages and the number printed on their invoice can't
   drift apart. */
export const STORE_WHATSAPP = normalisePhone(STORE.phone);

export const waLink = (phone, message) =>
  `https://wa.me/${normalisePhone(phone)}?text=${encodeURIComponent(message)}`;

/* ---------------------------------------------
   Customer → store: "I'd like to place an order"
--------------------------------------------- */
export const buildOrderMessage = ({
  orderNumber,
  customerName,
  customerPhone,
  address,
  items,
  subtotal,
  discount = 0,
  couponCode = null,
  total,
}) => {
  const lines = items
    .map(
      ({ product, quantity }) =>
        `• ${product.name} (${product.planet}) × ${quantity} — ${inr(
          product.price * quantity
        )}`
    )
    .join('\n');

  // Show the sum only when a code took something off it — an ordinary
  // order reads better with a single Total line.
  const discounted = Number(discount) > 0;

  return [
    'Hello, I want to place an order:',
    '',
    `Order ID: ${orderNumber}`,
    '',
    `Name: ${customerName}`,
    `Phone: ${customerPhone}`,
    address ? `Address: ${address}` : null,
    '',
    'Items:',
    lines,
    '',
    discounted ? `Subtotal: ${inr(subtotal)}` : null,
    discounted
      ? `Discount${couponCode ? ` (${couponCode})` : ''}: −${inr(discount)}`
      : null,
    `Total: ${inr(total)}`,
    '',
    'Please guide me for payment and delivery.',
  ]
    .filter((line) => line !== null)
    .join('\n');
};

/* ---------------------------------------------
   Store → customer: status updates
--------------------------------------------- */
const ITEM_LINES = (order) =>
  (order.order_items || [])
    .map(
      (it) =>
        `• ${it.product_name} × ${it.quantity} — ${inr(it.total_price)}`
    )
    .join('\n');

/* ---------------------------------------------
   Store → vendor: pack this and post it

   The vendor holds the stock and ships straight to the customer, so
   this message is really a parcel label: what to put in the box, and
   the To/From block to print and stick on it. Kept deliberately plain
   — anything clever here ends up photocopied onto a package.
--------------------------------------------- */

/* "Tel. 8929363150" — a label wants the plain local number, not
   "+91 87965 92272" with its spaces and country code. */
const localPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
};

/* composeAddress() builds "house, street, Near X, City, State - 110086"
   and joins it with ", ". Splitting on every comma therefore tears the
   last line in two — "New Delhi" above "Delhi - 110086". This puts
   that pair back together so the label reads the way an envelope
   should. Anything that doesn't match falls through untouched. */
export const addressLabelLines = (address) => {
  const parts = String(address || '')
    .split(/,\s*/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length >= 2 && /\s-\s*\d{5,6}$/.test(parts[parts.length - 1])) {
    const pin = parts.pop();
    const city = parts.pop();
    parts.push(`${city}, ${pin}`);
  }

  return parts;
};

/**
 * @param order      row from public.orders with order_items attached
 * @param photoUrls  optional "Product name → image URL" pairs. WhatsApp
 *                   links can't carry an attachment, so a photo can
 *                   only ever be a link the vendor taps.
 */
export const buildVendorMessage = (order, photoUrls = []) => {
  const items = (order.order_items || [])
    .map((item) => {
      const qty = Number(item.quantity) || 0;
      return `${item.product_name} - ${qty} unit${qty === 1 ? '' : 's'}`;
    })
    .join('\n');

  const photos = photoUrls
    .filter((p) => p.url)
    .map((p) => `${p.name} — ${p.url}`);

  return [
    items,
    '',
    'To,',
    order.customer_name || '',
    ...addressLabelLines(order.shipping_address),
    `Tel. ${localPhone(order.customer_phone)}`,
    '',
    'From',
    STORE.name,
    `Tel. ${localPhone(STORE.phone)}`,
    // Kept below the label block so it can be photocopied without them.
    ...(photos.length ? ['', 'Photos:', ...photos] : []),
  ].join('\n');
};

/** Where dispatch instructions go. Empty until VENDOR.phone is set. */
export const vendorWaLink = (message) =>
  VENDOR.phone ? waLink(VENDOR.phone, message) : null;

/* ---------------------------------------------
   Store → customer: "here is where to pay"

   Sent while the order is still pending, BEFORE any invoice exists —
   an invoice here is a receipt and is only raised once the money has
   actually arrived.
--------------------------------------------- */
export const buildPaymentRequestMessage = (order) => {
  const name = order.customer_name || 'there';
  const items = ITEM_LINES(order);

  return [
    `Hello ${name}, this is ${STORE.name}.`,
    '',
    `Thank you for your order ${order.order_number}.`,
    '',
    items ? `Items:\n${items}` : null,
    '',
    Number(order.discount_amount) > 0
      ? `Subtotal: ${inr(order.total_amount)}`
      : null,
    Number(order.discount_amount) > 0
      ? `Discount${order.coupon_code ? ` (${order.coupon_code})` : ''}: −${inr(
          order.discount_amount
        )}`
      : null,
    `Amount payable: ${inr(order.final_amount)}`,
    '',
    'Payment details:',
    `UPI ID: ${STORE.upiId}`,
    `Amount: ${inr(order.final_amount)}`,
    `Please add your order number ${order.order_number} in the payment note.`,
    '',
    'Once we verify your payment, we will send your order confirmation ' +
      'along with the invoice.',
    '',
    `— ${STORE.name}`,
  ]
    .filter((line) => line !== null)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
};

export const buildStatusMessage = (order, status) => {
  const name = order.customer_name || 'there';
  const items = ITEM_LINES(order);
  const head = `Hello ${name}, this is ${STORE.name}.`;

  const body = {
    confirmed: [
      `✅ Payment received — your order ${order.order_number} is CONFIRMED.`,
      '',
      items ? `Items:\n${items}` : null,
      '',
      `Total paid: ${inr(order.final_amount)}`,
      /* Only mentioned once an invoice actually exists. A wa.me link
         cannot carry a file, so the PDF is attached by hand — saying
         "attached" when nothing is would be worse than saying nothing. */
      order.invoice_number
        ? `Invoice: ${order.invoice_number} (attached)`
        : null,
      '',
      'Your order is being packed and will be dispatched shortly.',
    ],
    shipped: [
      `📦 Good news — your order ${order.order_number} has been SHIPPED.`,
      '',
      items ? `Items:\n${items}` : null,
      '',
      'It should reach you in 3–5 business days.',
    ],
    delivered: [
      `🎉 Your order ${order.order_number} has been DELIVERED.`,
      '',
      'We hope you love your gemstone. Wear it well!',
      '',
      'If you have a moment, we would be grateful for your feedback.',
    ],
    cancelled: [
      `Your order ${order.order_number} has been cancelled.`,
      '',
      'If this was not expected, please reply here and we will help.',
    ],
    pending: [
      `We have received your order ${order.order_number} and are reviewing it.`,
      '',
      'We will confirm shortly.',
    ],
  }[status] || [`Update on your order ${order.order_number}: ${status}.`];

  return [head, '', ...body.filter((l) => l !== null), '', `— ${STORE.name}`]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
};
